import {
  Box,
  Divider,
  Paper,
  Theme,
  makeStyles,
  useMediaQuery,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
} from '@material-ui/core'
import {
  AccountBalanceWallet,
  Archive,
  ArrowForward,
  CenterFocusStrong,
  Keyboard,
  PhoneAndroid,
  Storage,
  Update,
  RadioButtonChecked,
  MeetingRoom,
} from '@material-ui/icons'
import FormatSizeIcon from '@mui/icons-material/FormatSize'
import AddIcon from '@mui/icons-material/Add'
import { DoorFront } from '@mui/icons-material'
import EditIcon from '@mui/icons-material/Edit'
import EditOffIcon from '@mui/icons-material/EditOff'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import SortIcon from '@mui/icons-material/Sort'
import camelcaseKeys from 'camelcase-keys'
import classNames from 'classnames'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  DeleteButton,
  SaveButton,
  Show,
  ShowProps,
  SimpleForm,
  TextField,
  TitleProps,
  Toolbar,
  UPDATE,
  sanitizeFetchType,
  useDataProvider,
  useMutation,
  useNotify,
  useQueryWithStore,
  useTranslate,
} from 'react-admin'
import { useHistory, useLocation } from 'react-router'
import { Rnd } from 'react-rnd'
import { DeviceManagementContainerDto } from '../../../core/dto/device/device-management/device-management-containers/DeviceManagementContainer.dto'
import { ResourceName } from '../../../core/ResourceName'
import Button from '../../common/customized-mui-components/Button'
import ShowButton from '../../common/customized-mui-components/ShowButton'
import { getContrastColor } from '../../../core/common/visual.utils'
import { CreateDialog } from '../../common/customized-ra-components/CreateDialog'
import FilteredReferenceInput from '../../common/FilteredReferenceInput'
import { ManagementItemDeviceTypes } from '../../../core/enum/ManagementItemDeviceTypes'
import { DeviceManagementItemDto } from '../../../core/dto/device/device-management/device-management-items/DeviceManagementItem.dto'
import { DeviceTypes } from '../../../core/enum/DeviceTypes'
import { DoorWorkingModeManagementButton } from './doors/DoorWorkingModeManagementButton'
import ElementResizeListener from '../../common/ElementResizeListener'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import { Authority } from '../../../core/auth/Authority'
import { DoorWorkingModes } from '../../../core/enum/DoorWorkingModes'
import { DoorWorkingModeById } from '../../../core/devices/doors/doors.utils'
import { DoorReleaseManagementButton } from './doors/DoorReleaseManagementButton'
import { DoorTypes } from '../../../core/enum/DoorTypes'
import ImagePolygonsOverlay from '../../common/ImagePolygonsOverlay'
import { TreeWithDetails } from '../../../lib/@react-admin/ra-tree/esm/src'
import { IconSizes } from '../../../core/enum/IconSizes'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    contain: 'content',
    height: '80vh',
  },
  imageStyle: {
    height: '100%',
  },
  itemOpen: {
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
  },
  itemOpenDesktopdefault: {
    minWidth: theme.spacing(4),
    minHeight: theme.spacing(4),
  },
  itemOpenDesktopsmall: {
    minWidth: theme.spacing(3.5),
    minHeight: theme.spacing(3.5),
  },
  itemOpenDesktoplarge: {
    minWidth: theme.spacing(5),
    minHeight: theme.spacing(5),
  },
  itemOpenMobile: {
    minWidth: theme.spacing(3.5),
    minHeight: theme.spacing(3.5),
  },
  itemClosed: {
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
  },
  itemClosedDesktopdefault: {
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
  itemClosedDesktopsmall: {
    width: theme.spacing(3.3),
    height: theme.spacing(3),
  },
  itemClosedDesktoplarge: {
    width: theme.spacing(5),
    height: theme.spacing(5),
  },
  itemClosedMobile: {
    width: theme.spacing(3.5),
    height: theme.spacing(3.5),
  },
  smallFontSize: {
    fontSize: '0.8125rem',
  },
  defaultFontSize: {
    fontSize: '0.875rem',
  },
  largeFontSize: {
    fontSize: '1.1rem',
  },
  itemDetails: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '100%',
  },
  sideContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(0.5),
  },
  // sideContainerClosed: {
  //   display: 'flex',
  //   alignItems: 'center',
  //   justifyContent: 'center',
  //   padding: theme.spacing(0.5),
  // },
  centerContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
    userSelect: 'none',
    width: '100%',
    '& > span': {
      userSelect: 'none',
    },
  },
  grabCursor: {
    cursor: 'grab',
  },
  noIcon: {
    width: 24,
  },
  noIconMobile: {
    width: 20,
  },
  dividerVertical: {
    width: 1,
    height: '100%',
  },
  dividerHorizontal: {
    height: 1,
    width: '100%',
  },
  topContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
  },
  topContainerDesktopdefault: {
    height: theme.spacing(4),
  },
  topContainerDesktopsmall: {
    height: theme.spacing(3),
  },
  topContainerDesktoplarge: {
    height: theme.spacing(5),
  },
  topContainerMobile: {
    height: theme.spacing(3.5),
  },
  bottomContainer: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
    '& > button': {
      alignItems: 'start',
      justifyContent: 'start',
    },
    '& > a': {
      alignItems: 'start',
      justifyContent: 'start',
    },
  },
  deviceLabel: {
    fontWeight: 'bold',
  },
  show: {
    '& > div': {
      justifyContent: 'center',
      alignItems: 'center',
      '& > div': {
        flex: 'initial !important',
      },
    },
  },
  generalActionButton: {
    padding: '4px',
  },
  generalActionDiv: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  rightControls: {
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginRight: '10px',
  },
  formControlRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  containerShow: {
    display: 'flex',
    height: '100%',
    '& .rc-tree-title > div > button': {
      visibility: 'hidden',
    },
  },
  tree: {
    overflowY: 'auto',
  },
  hide: {
    display: 'none',
  },
  sizeSelect: {
    '& > div': {
      height: '32px',
      '& > div': {
        fontWeight: 500,
        fontSize: '0.8125rem',
      },
    },
  },
  timer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  select: {
    '& > div': {
      padding: theme.spacing(1),
    },
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  iconError: {
    color: theme.palette.error.dark,
    stroke: theme.palette.common.white,
    strokeWidth: 0.5,
  },
}))

const FormToolbar = ({ ...props }) => {
  const { handleClose, ...rest } = props

  return (
    <Toolbar {...rest}>
      <SaveButton redirect={false} onClick={handleClose} />
    </Toolbar>
  )
}

interface DeviceManagementContainerTitleProps extends TitleProps {
  record?: DeviceManagementContainerDto
}

const ShowTitle = (data: DeviceManagementContainerTitleProps) => {
  const { record: container } = data
  const t = useTranslate()
  return (
    <span>
      {t('resources.device-management-containers.titles.show')}: {container?.id}
      . {container?.name}
    </span>
  )
}

const ContainerRefreshSelect = ({ ...props }) => {
  const { intervalId, intervalLength, setIntervalId, setIntervalLength } = props
  const translate = useTranslate()
  const location = useLocation()
  const history = useHistory()
  const classes = useStyles()

  const handleSetIntervalLength = (e) => {
    setIntervalLength(e?.target?.value)
    if (intervalId) {
      clearInterval(intervalId)
      setIntervalId(undefined)
    }
    if (e?.target?.value === 0) {
      const params = new URLSearchParams(location.search)
      params.delete('interval')
      history.replace(`${location.pathname}?${params.toString()}`)
    }
  }

  return (
    <div className={classes.timer}>
      <Tooltip
        title={translate('resources.stats-dynamic-statistics.fields.interval')}
      >
        <Update />
      </Tooltip>
      <Select
        value={intervalLength}
        onChange={handleSetIntervalLength}
        label="timeout"
      >
        {[0, 30, 60, 300].map((interval: number) => (
          <MenuItem key={interval} value={interval}>
            {translate(`resources.enums.TimeIntervals.${interval}`)}
          </MenuItem>
        ))}
      </Select>
    </div>
  )
}

const IconSizeSelect = ({ ...props }) => {
  const { itemSize, setItemSize, patchSettings } = props
  const translate = useTranslate()

  return (
    <Select
      labelId="item-size-select-label"
      value={itemSize}
      onChange={(e) => {
        setItemSize(e.target.value as IconSizes)
        patchSettings({
          patchIconValue: (e.target.value as IconSizes).toUpperCase(),
        })
      }}
      MenuProps={{
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        transformOrigin: { vertical: 'top', horizontal: 'left' },
        getContentAnchorEl: null,
      }}
      renderValue={(val) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FormatSizeIcon fontSize="small" />
          <span style={{ lineHeight: 1 }}>
            {translate(
              `resources.${ResourceName.DEVICE_MANAGEMENT_CONTAINERS}.sizes.${val}`,
            ).toUpperCase()}
          </span>
        </div>
      )}
    >
      <MenuItem value="small">
        <span>
          {translate(
            `resources.${ResourceName.DEVICE_MANAGEMENT_CONTAINERS}.sizes.small`,
          ).toUpperCase()}
        </span>
      </MenuItem>
      <MenuItem value="default">
        <span>
          {translate(
            `resources.${ResourceName.DEVICE_MANAGEMENT_CONTAINERS}.sizes.default`,
          ).toUpperCase()}
        </span>
      </MenuItem>
      <MenuItem value="large">
        <span>
          {translate(
            `resources.${ResourceName.DEVICE_MANAGEMENT_CONTAINERS}.sizes.large`,
          ).toUpperCase()}
        </span>
      </MenuItem>
    </Select>
  )
}

const DeviceManagementItemIcon = ({ ...props }) => {
  const {
    // detailsOpen,
    deviceType,
    device,
    disabled,
    handleDetails,
    iconSize = 'default',
  } = props
  const classes = useStyles()

  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const icon = () => {
    switch (deviceType) {
      case ManagementItemDeviceTypes.ENGINE:
        return (
          <AccountBalanceWallet fontSize={!smallScreen ? iconSize : 'small'} />
        )
      case ManagementItemDeviceTypes.PALM:
        return <PhoneAndroid fontSize={!smallScreen ? iconSize : 'small'} />
      case ManagementItemDeviceTypes.DETECTOR:
        return (
          <CenterFocusStrong fontSize={!smallScreen ? iconSize : 'small'} />
        )
      case ManagementItemDeviceTypes.PASSAGE:
        return <ArrowForward fontSize={!smallScreen ? iconSize : 'small'} />
      case ManagementItemDeviceTypes.OFFLINE_SERVER:
        return <Storage fontSize={!smallScreen ? iconSize : 'small'} />
      case ManagementItemDeviceTypes.DEPOSITOR:
        return <Archive />
      case ManagementItemDeviceTypes.DOOR: {
        const jsonStatus =
          device?.jsonStatus &&
          camelcaseKeys(JSON.parse(device?.jsonStatus), { deep: true })
        if (jsonStatus?.safeButtonPressed)
          return (
            <RadioButtonChecked
              className={classes.iconError}
              fontSize={!smallScreen ? iconSize : 'small'}
            />
          )
        if (jsonStatus?.isDoorOpen)
          return <MeetingRoom fontSize={!smallScreen ? iconSize : 'small'} />
        return <DoorFront fontSize={!smallScreen ? iconSize : 'small'} />
      }
      case ManagementItemDeviceTypes.TERMINAL:
        return <Keyboard fontSize={!smallScreen ? iconSize : 'small'} />
      default:
        return (
          <div
            className={!smallScreen ? classes.noIcon : classes.noIconMobile}
          />
        )
    }
  }

  return (
    <Box
      className={classes.sideContainer}
      onClick={!disabled ? handleDetails : undefined}
      style={{
        cursor: !disabled ? 'pointer' : 'auto',
      }}
    >
      {icon()}
    </Box>
  )
}

const DeviceManagementItemLabel = ({ ...props }) => {
  const { device, sizeClass, editMode } = props
  const classes = useStyles()

  return editMode ? (
    <div
      className={`${classNames(
        classes.centerContainer,
        classes.grabCursor,
      )} draggable-item-title ant-modal-header`}
      id="draggable-item-title"
    >
      <TextField
        source="name"
        record={device}
        className={classNames(classes.deviceLabel, sizeClass)}
      />
    </div>
  ) : (
    <div
      className={`${classes.centerContainer} ant-modal-header`}
      id="draggable-item-title"
    >
      <TextField
        source="name"
        record={device}
        className={classNames(classes.deviceLabel, sizeClass)}
      />
    </div>
  )
}

const getDeviceResource = (deviceType: ManagementItemDeviceTypes) => {
  switch (deviceType) {
    case ManagementItemDeviceTypes.BARRIER:
      return ResourceName.BARRIERS
    case ManagementItemDeviceTypes.DEPOSITOR:
      return ResourceName.DEPOSITORS
    case ManagementItemDeviceTypes.DETECTOR:
      return ResourceName.DETECTORS
    case ManagementItemDeviceTypes.DOOR:
      return ResourceName.DOORS
    case ManagementItemDeviceTypes.ENGINE:
      return ResourceName.ENGINES
    case ManagementItemDeviceTypes.OFFLINE_SERVER:
      return ResourceName.OFFLINE_SERVERS
    case ManagementItemDeviceTypes.PALM:
      return ResourceName.PALMS
    case ManagementItemDeviceTypes.PASSAGE:
      return ResourceName.PASSAGES
    case ManagementItemDeviceTypes.TERMINAL:
      return ResourceName.TERMINALS
    default:
      return ResourceName.DEVICES_BASE
  }
}

const DeviceManagementItemActions = ({ ...props }) => {
  const { color, device, item, fontSizeClass, editMode } = props
  const { deviceType } = item
  const resource = getDeviceResource(deviceType)
  const classes = useStyles()
  const hasAuthority = useHasAuthority()

  const jsonStatus =
    device?.jsonStatus &&
    camelcaseKeys(JSON.parse(device?.jsonStatus), { deep: true })

  return (
    <div className={classes.bottomContainer}>
      {editMode &&
        deviceType === ManagementItemDeviceTypes.DOOR &&
        device?.type !== DoorTypes.NO_READERS && (
          <DoorWorkingModeManagementButton
            color={color}
            door={device}
            jsonStatus={jsonStatus}
            fontSizeClass={fontSizeClass}
          />
        )}
      {editMode &&
        deviceType === ManagementItemDeviceTypes.DOOR &&
        device?.type === DoorTypes.NO_READERS && (
          <DoorReleaseManagementButton
            color={color}
            door={device}
            jsonStatus={jsonStatus}
            fontSizeClass={fontSizeClass}
          />
        )}
      {((deviceType === ManagementItemDeviceTypes.BARRIER &&
        hasAuthority(Authority.VIEW_BARRIERS)) ||
        (deviceType === ManagementItemDeviceTypes.ENGINE &&
          hasAuthority(Authority.VIEW_ENGINES)) ||
        (deviceType === ManagementItemDeviceTypes.PALM &&
          hasAuthority(Authority.VIEW_PALMS)) ||
        (deviceType === ManagementItemDeviceTypes.DETECTOR &&
          hasAuthority(Authority.VIEW_DETECTORS)) ||
        (deviceType === ManagementItemDeviceTypes.PASSAGE &&
          hasAuthority(Authority.VIEW_PASSAGES)) ||
        (deviceType === ManagementItemDeviceTypes.OFFLINE_SERVER &&
          hasAuthority(Authority.VIEW_OFFLINE_SERVERS)) ||
        (deviceType === ManagementItemDeviceTypes.DEPOSITOR &&
          hasAuthority(Authority.VIEW_DEPOSITORS)) ||
        (deviceType === ManagementItemDeviceTypes.DOOR &&
          hasAuthority(Authority.VIEW_DOORS)) ||
        (deviceType === ManagementItemDeviceTypes.TERMINAL &&
          hasAuthority(Authority.VIEW_TERMINALS))) && (
        <ShowButton
          className={fontSizeClass}
          classes={{ label: fontSizeClass }}
          record={device}
          id={item?.deviceId}
          resource={resource}
          basePath={`/${resource}`}
          fullWidth
          style={{ color: getContrastColor(color) }}
          label="resources.device-management-items.actions.show_device"
        />
      )}
      {editMode && hasAuthority(Authority.DELETE_DEVICE_MANAGEMENT_ITEMS) ? (
        <>
          <Divider className={classes.dividerHorizontal} />
          <DeleteButton
            className={fontSizeClass}
            record={item}
            id={item?.id}
            resource={ResourceName.DEVICE_MANAGEMENT_ITEMS}
            basePath="/device-management-containers"
            redirect={false}
            fullWidth
            style={{ color: getContrastColor(color) }}
            label="resources.device-management-items.actions.remove_item"
            mutationMode="optimistic"
            disabled={!editMode}
          />
        </>
      ) : undefined}
    </div>
  )
}

const getItemColor = (item: DeviceManagementItemDto, palette: any) => {
  const { deviceType, device } = item

  if (deviceType === ManagementItemDeviceTypes.DEPOSITOR)
    return palette?.deviceManagement.blue

  if (deviceType === ManagementItemDeviceTypes.DETECTOR)
    return palette?.deviceManagement.blue

  if (deviceType === ManagementItemDeviceTypes.DOOR) {
    const jsonStatus =
      device?.jsonStatus &&
      camelcaseKeys(JSON.parse(device.jsonStatus), { deep: true })

    if (jsonStatus?.safeButtonPressed) return palette?.deviceManagement.black

    if (
      DoorWorkingModeById[jsonStatus?.workingMode] ===
      DoorWorkingModes.DoorReleased
    )
      return palette?.deviceManagement.green
    if (
      DoorWorkingModeById[jsonStatus?.workingMode] ===
      DoorWorkingModes.CardVerification
    )
      return palette?.deviceManagement.blue
    if (
      DoorWorkingModeById[jsonStatus?.workingMode] ===
      DoorWorkingModes.FirstReaderDisabled
    )
      return palette?.deviceManagement.pink
    if (
      DoorWorkingModeById[jsonStatus?.workingMode] ===
      DoorWorkingModes.SecondReaderDisabled
    )
      return palette?.deviceManagement.pink
    if (
      DoorWorkingModeById[jsonStatus?.workingMode] ===
      DoorWorkingModes.AllReadersDisabled
    )
      return palette?.deviceManagement.red
    return palette?.deviceManagement.black
  }

  if (deviceType === ManagementItemDeviceTypes.ENGINE)
    return palette?.deviceManagement.blue

  if (deviceType === ManagementItemDeviceTypes.OFFLINE_SERVER)
    return palette?.deviceManagement.blue

  if (deviceType === ManagementItemDeviceTypes.PALM)
    return palette?.deviceManagement.blue

  if (deviceType === ManagementItemDeviceTypes.PASSAGE)
    return palette?.deviceManagement.blue

  if (deviceType === ManagementItemDeviceTypes.TERMINAL)
    return palette?.deviceManagement.blue

  return palette?.deviceManagement.blue
}

const DeviceManagementItemView = ({ ...props }) => {
  const {
    item,
    refetchItems,
    containerWidth,
    containerHeight,
    detailsOpen,
    setDetailsOpen,
    itemSize,
    editMode,
  } = props
  const classes = useStyles()
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const hasAuthority = useHasAuthority()
  const [mutate, { loading }] = useMutation()
  const [itemLayer, setItemLayer] = useState<number>(100)
  const [fontSizeClass, setFontSizeClass] = useState<string>()

  const { device } = item

  const color = getItemColor(item, theme?.palette)

  const getFontSizeClass = (
    fontSize: 'small' | 'default' | 'large' | string,
  ): string => {
    switch (fontSize) {
      case 'small':
        return classes.smallFontSize
      case 'large':
        return classes.largeFontSize
      default:
        return classes.defaultFontSize
    }
  }

  useEffect(() => {
    setFontSizeClass(getFontSizeClass(itemSize))
    // eslint-disable-next-line
  }, [itemSize])

  const viewStyle = () => ({
    background: color || undefined,
    color: color ? getContrastColor(color) : undefined,
  })

  const handleDetails = () => {
    if (detailsOpen === item?.id) setDetailsOpen(0)
    else setDetailsOpen(item?.id)
  }

  useEffect(() => {
    if (detailsOpen === item?.id && itemLayer !== 200) setItemLayer(200)
    if (detailsOpen !== item?.id && itemLayer !== 100) setItemLayer(100)
  }, [detailsOpen, item, itemLayer, setItemLayer])

  const onStop = (e, data) => {
    // getting x and y coordinates from the eventHandler
    let { x: lastX, y: lastY } = data
    lastX = (lastX * 10000) / (containerWidth as number)
    lastY = (lastY * 10000) / (containerHeight as number)
    let xPosition
    let yPosition

    // converting the x and y coordinates to be between 0 and 10000
    if (lastX > 0 && lastX < 10000) xPosition = lastX
    else if (lastX < 0) xPosition = 0
    else xPosition = 10000

    if (lastY > 0 && lastY < 10000) {
      yPosition = lastY
    } else if (lastY < 0) yPosition = 0
    else yPosition = 10000

    // patching item x and y coordinates
    mutate({
      type: sanitizeFetchType(UPDATE),
      resource: ResourceName.DEVICE_MANAGEMENT_ITEMS,
      payload: {
        id: item?.id,
        data: {
          ...item,
          xPosition,
          yPosition,
        },
        previousData: item,
      },
    })

    // apply position immediately to avoid element bouncing during refetch
    item.xPosition = xPosition
    item.yPosition = yPosition

    refetchItems()
  }

  return (
    <Rnd
      key={item?.id}
      position={{
        x: (item?.xPosition / 10000) * (containerWidth as number),
        y: (item?.yPosition / 10000) * (containerHeight as number),
      }}
      enableResizing={false}
      onDragStop={onStop}
      dragAxis={
        !loading && hasAuthority(Authority.EDIT_DEVICE_MANAGEMENT_ITEMS)
          ? 'both'
          : 'none'
      }
      dragHandleClassName="draggable-item-title"
      style={{
        zIndex: itemLayer,
      }}
    >
      <Paper
        className={classNames(
          detailsOpen === item?.id && classes.itemOpen,
          detailsOpen === item?.id &&
            !smallScreen &&
            (classes as any)[`itemOpenDesktop${itemSize}`],
          detailsOpen === item?.id && smallScreen && classes.itemOpenMobile,
          detailsOpen !== item?.id && classes.itemClosed,
          detailsOpen !== item?.id &&
            !smallScreen &&
            (classes as any)[`itemClosedDesktop${itemSize}`],
          detailsOpen !== item?.id && smallScreen && classes.itemClosedMobile,
        )}
        style={viewStyle()}
      >
        <div
          className={classNames(
            classes.topContainer,
            !smallScreen && (classes as any)[`topContainerDesktop${itemSize}`],
            smallScreen && classes.topContainerMobile,
          )}
        >
          <DeviceManagementItemIcon
            detailsOpen={detailsOpen}
            deviceType={item?.deviceType}
            device={device}
            handleDetails={handleDetails}
            disabled={!device || !color}
            iconSize={itemSize}
          />
          {detailsOpen === item?.id ? (
            <div className={classes.itemDetails}>
              <Divider
                orientation="vertical"
                className={classes.dividerVertical}
              />
              <DeviceManagementItemLabel
                device={device}
                sizeClass={fontSizeClass}
                editMode={editMode}
              />
            </div>
          ) : undefined}
        </div>
        {detailsOpen === item?.id ? (
          <DeviceManagementItemActions
            color={color}
            device={device}
            item={item}
            fontSizeClass={fontSizeClass}
            editMode={editMode}
          />
        ) : undefined}
      </Paper>
    </Rnd>
  )
}

const DeviceManagementContainerView = ({ ...props }) => {
  const { record, treeOpened, setTreeOpened, resource } = props
  const { backgroundImageId, id: recordId, itemsIds } = record
  const t = useTranslate()
  const classes = useStyles()
  const notify = useNotify()
  const theme = useTheme()
  const [mutate] = useMutation()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const itemsContainerRef = useRef(null)
  const hasAuthority = useHasAuthority()

  const [detailsOpen, setDetailsOpen] = useState<number>(0)
  const [containerWidth, setContainerWidth] = useState(0)
  const [containerHeight, setContainerHeight] = useState(0)
  const [editMode, setEditMode] = useState(false)
  const [itemSize, setItemSize] = useState<IconSizes>(IconSizes.default)

  useQueryWithStore(
    {
      type: 'getSettings',
      resource: ResourceName.CURRENT_USER,
      payload: {},
    },
    {
      onSuccess: (data) => {
        setTreeOpened(data.data?.viewState_DeviceManagement_TreeOpened)
        setItemSize(
          data.data?.viewState_DeviceManagement_IconSize?.toLowerCase(),
        )
      },
    },
  )

  const patchSettings = (patchSettingsValues) => {
    const { patchIconValue, patchTreeOpened } = patchSettingsValues
    mutate(
      {
        type: sanitizeFetchType('patchSettings'),
        resource: ResourceName.CURRENT_USER,
        payload: {
          viewState_DeviceManagement_IconSize: patchIconValue,
          viewState_DeviceManagement_TreeOpened: patchTreeOpened,
        },
      },
      {
        returnPromise: true,
        onFailure: (err) => notify(err?.message, 'error'),
      },
    )
  }

  // fetching container background
  const { data: backgroundImage } = useQueryWithStore({
    type: sanitizeFetchType('downloadFile'),
    resource: ResourceName.FILES,
    payload: {
      id: backgroundImageId,
    },
  })

  const image = useMemo(
    () =>
      backgroundImage && backgroundImage?.blob
        ? URL.createObjectURL(backgroundImage?.blob?.body)
        : undefined,
    [backgroundImage],
  )

  const canViewItems = hasAuthority(Authority.VIEW_DEVICE_MANAGEMENT_ITEMS)

  const dataProvider = useDataProvider()
  const [items, setItems] = useState<DeviceManagementItemDto[] | undefined>(
    undefined,
  )

  const refetchItems = useCallback(() => {
    if (!canViewItems) {
      // do nothing for unauthorized users
      return Promise.resolve({ data: [], total: 0 })
    }

    return dataProvider
      .getList(ResourceName.DEVICE_MANAGEMENT_ITEMS, {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: { containerId: recordId },
      })
      .then((response) => {
        if (response) setItems(response.data as any)
        return response
      })
      .catch((err) => {
        throw err
      })
  }, [dataProvider, canViewItems, recordId])

  useEffect(() => {
    if (canViewItems) {
      refetchItems().catch((err) => {
        throw err
      })
    } else {
      setItems(undefined)
    }
  }, [canViewItems, refetchItems])

  useEffect(() => {
    if (canViewItems) {
      refetchItems()
    } else {
      setItems(undefined)
    }
  }, [canViewItems, refetchItems, itemsIds])

  const [createOpen, setCreateOpen] = useState<boolean>(false)

  const handleCloseCreate = () => {
    setCreateOpen(false)
    refetchItems()
  }

  const handleOpenCreate = () => {
    setCreateOpen(true)
  }

  const handleContainerSizeChange = (e) => {
    if (e.currentTarget) {
      setContainerWidth(e.currentTarget.innerWidth)
      setContainerHeight(e.currentTarget.innerHeight)
    }
  }

  function useOutsideAlerter(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (
          ref.current &&
          ref.current.contains(event.target) &&
          event.target.id === 'linksOverlaySVG'
        ) {
          setDetailsOpen(0)
        }
      }
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [ref])
  }
  const wrapperRef = useRef(null)
  useOutsideAlerter(wrapperRef)

  const location = useLocation()
  const params = new URLSearchParams(location?.search)
  const history = useHistory()

  const [intervalLength, setIntervalLength] = useState<number>(
    parseInt(params?.get('interval') ?? '0', 10),
  )
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | undefined>(
    undefined,
  )

  useEffect(() => {
    if (!intervalId && intervalLength !== 0) {
      setIntervalId(setInterval(refetchItems, intervalLength * 1000))
      params.set('interval', intervalLength.toString())
      history.replace(`${location.pathname}?${params.toString()}`)
    }
    if (intervalLength === 0) {
      clearInterval(intervalId as NodeJS.Timeout)
      setIntervalId(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    history,
    intervalId,
    intervalLength,
    location,
    refetchItems,
    setIntervalId,
  ])

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }} ref={wrapperRef}>
      <div className={classes.generalActionDiv}>
        {(hasAuthority(Authority.EDIT_DEVICE_MANAGEMENT_ITEMS) ||
          hasAuthority(Authority.EDIT_DEVICE_MANAGEMENT_LINKS)) && (
          <div id="actions" className={classes.generalActionButton}>
            <CreateDialog
              basePath="/device-management-containers"
              handleClose={handleCloseCreate}
              resource={ResourceName.DEVICE_MANAGEMENT_ITEMS}
              open={createOpen}
              title={t('resources.device-management-items.titles.create')}
            >
              <SimpleForm
                initialValues={{
                  containerId: record?.id,
                  xPosition: 5000,
                  yPosition: 5000,
                }}
                redirect={false}
                toolbar={<FormToolbar handleClose={handleCloseCreate} />}
              >
                <FilteredReferenceInput
                  source="deviceId"
                  defaultValue={null}
                  reference={ResourceName.DEVICES_BASE}
                  sort={{ field: 'name', order: 'ASC' }}
                  perPage={smallScreen ? 5 : 15}
                  allowEmpty
                  filterSource="name"
                  filter={{
                    deviceTypes: [DeviceTypes.DOORS],
                  }}
                  selectWithPaginationInputProps={{
                    optionText: 'name',
                    showFilter: true,
                  }}
                />
              </SimpleForm>
            </CreateDialog>
            {resource === ResourceName.DEVICE_MANAGEMENT_CONTAINERS && (
              <Button
                color="inherit"
                label={
                  treeOpened
                    ? t(
                        'resources.device-management-containers.actions.hideMapTree',
                      )
                    : t(
                        'resources.device-management-containers.actions.showMapTree',
                      )
                }
                onClick={() => {
                  setTreeOpened((prev: boolean) => !prev)
                  patchSettings({ patchTreeOpened: !treeOpened })
                }}
              >
                {treeOpened ? <ChevronLeftIcon /> : <SortIcon />}
              </Button>
            )}
            {hasAuthority(Authority.EDIT_DEVICE_MANAGEMENT_LINKS) && (
              <Button
                color="inherit"
                onClick={() => setEditMode((prev) => !prev)}
                label={
                  editMode
                    ? t(
                        `resources.${ResourceName.DEVICE_MANAGEMENT_CONTAINERS}.actions.exitEditMode`,
                      )
                    : t(
                        `resources.${ResourceName.DEVICE_MANAGEMENT_CONTAINERS}.actions.enterEditMode`,
                      )
                }
              >
                {editMode ? <EditOffIcon /> : <EditIcon />}
              </Button>
            )}
            {editMode &&
              hasAuthority(Authority.CREATE_DEVICE_MANAGEMENT_ITEMS) && (
                <Button
                  // color="inherit"
                  onClick={handleOpenCreate}
                  label={t(
                    `resources.${ResourceName.DEVICE_MANAGEMENT_CONTAINERS}.actions.addItem`,
                  )}
                >
                  <AddIcon />
                </Button>
              )}
          </div>
        )}
        <div className={classes.rightControls}>
          <div style={{ display: 'contents', alignItems: 'center' }}>
            <FormControl
              className={`${classes.sizeSelect} ${classes.formControlRow}`}
              size="small"
            >
              <IconSizeSelect
                itemSize={itemSize}
                setItemSize={setItemSize}
                patchSettings={patchSettings}
              />
            </FormControl>
            <FormControl
              className={`${classes.sizeSelect} ${classes.formControlRow}`}
              size="small"
            >
              <ContainerRefreshSelect
                intervalId={intervalId}
                intervalLength={intervalLength}
                setIntervalId={setIntervalId}
                setIntervalLength={setIntervalLength}
              />
            </FormControl>
          </div>
        </div>
      </div>
      <Divider />
      <div>
        <div
          id="itemsContainer"
          ref={itemsContainerRef}
          className={classes.container}
          style={{ position: 'relative' }}
        >
          <ElementResizeListener onResize={handleContainerSizeChange} />
          <img
            src={image}
            className={classes.imageStyle}
            alt="devices-management-background"
          />

          {/* Links */}
          <ImagePolygonsOverlay
            resource={ResourceName.DEVICE_MANAGEMENT_CONTAINERS}
            recordId={recordId}
            containerRef={itemsContainerRef}
            containerWidth={containerWidth}
            containerHeight={containerHeight}
            editMode={editMode}
          />
          {/* Items */}
          {hasAuthority(Authority.VIEW_DEVICE_MANAGEMENT_ITEMS) && (
            <>
              {items?.map((item) => (
                <DeviceManagementItemView
                  key={item.id}
                  item={item}
                  refetchItems={refetchItems}
                  containerWidth={containerWidth}
                  containerHeight={containerHeight}
                  detailsOpen={detailsOpen}
                  setDetailsOpen={setDetailsOpen}
                  itemSize={itemSize}
                  editMode={editMode}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export const DeviceManagementContainerShow = (props: ShowProps) => {
  const [treeOpened, setTreeOpened] = useState<boolean>(true)
  const classes = useStyles()

  return (
    <div className={classes.containerShow}>
      <div className={!treeOpened ? classes.hide : ''}>
        <TreeWithDetails
          className={classes.tree}
          titleField="name"
          linkTo="show"
          {...props}
        />
      </div>
      <Show
        {...props}
        actions={false}
        title={<ShowTitle />}
        className={classes.show}
      >
        <DeviceManagementContainerView
          treeOpened={treeOpened}
          setTreeOpened={setTreeOpened}
          {...props}
        />
      </Show>
    </div>
  )
}
